import {
  ArrowLeftIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Layout,
  LinkButton,
  PageHeading,
  Tooltip,
} from '@kira/checkpoint-shared-frontend';
import React, { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Button, { ButtonVariant } from 'components/Button/Button';
import RadioButton from 'components/RadioButton/RadioButton';
import TextArea from 'components/TextArea/TextArea';
import useCreateAssessment from 'hooks/useCreateAssessment';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';

enum FormFields {
  ASSESSMENT_NAME = 'name',
}

interface FormData {
  [FormFields.ASSESSMENT_NAME]: string;
}

const CreateAssessmentForm = () => {
  const invitationMethods = [
    { id: 1, name: 'Invite-only', value: false },
    { id: 2, name: 'Public', value: true },
  ];

  const [isPublic, setIsPublic] = useState<boolean>(invitationMethods[0].value);

  const navigate = useNavigate();
  const { showErrorNotification, showSuccessNotification } = useNotification();

  const { createAssessment, isLoading } = useCreateAssessment();
  const toolTipText = t(
    "Public assessments can be accessed by anyone with the assessment link. Invite-only assessments are accessible only to applicants who've been invited to participate. This setting can be changed in the Settings tab at any time."
  );

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.ASSESSMENT_NAME]: yup
          .string()
          .required(t('Assessment name is required'))
          .max(60, t('Assessment name cannot exceed 60 characters')),
      }),
    [FormFields.ASSESSMENT_NAME]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: {
      [FormFields.ASSESSMENT_NAME]: '',
    },
  });

  const onSubmit: SubmitHandler<FormData> = async ({ name }) => {
    await createAssessment(
      {
        name,
        isPublic,
      },
      {
        onSuccess: ({ uid: interviewUid }) => {
          navigate(`/interview/${interviewUid}/overview`);
          showSuccessNotification(
            t('Success'),
            t('Assessment created successfully')
          );
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while creating the assessment'),
            error.message
          );
        },
      }
    );

    reset();
  };

  return (
    <Layout layoutClassName="!bg-kira-blue-50">
      <LinkButton
        to="/"
        className="!prose-button-lg absolute left-5 top-3 flex w-fit items-center !bg-transparent !p-0 !text-kira-blue-800"
      >
        <ArrowLeftIcon className="mr-1 inline-block h-4 w-4" />
        {t('Back')}
      </LinkButton>
      <div className="mx-auto grid h-full w-full !bg-kira-blue-50">
        <div className="place-self-center text-center lg:w-4/5">
          <PageHeading
            primaryText="Async Assessment Details"
            secondaryText="2 OF 2"
            primaryTextClassName="!prose-display-1 !text-kira-slate-900"
            secondaryTextClassName="!prose-body-lg-reg !text-kira-slate-900"
          />
          <div className="mt-6 flex flex-col items-center justify-center gap-12 lg:flex-row">
            <div className="relative h-full w-full rounded-xl bg-white px-6 pb-4 pt-5 shadow-md lg:w-2/3 xl:w-1/2">
              <form
                data-testid="create-assessment-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <fieldset>
                  <div className="flex flex-col">
                    <div className="flex flex-1 flex-col md:flex-row">
                      <div className="flex-1 text-left">
                        <TextArea
                          label={t('Assessment Name')}
                          id={FormFields.ASSESSMENT_NAME}
                          value={watch(FormFields.ASSESSMENT_NAME)}
                          errors={errors[FormFields.ASSESSMENT_NAME]}
                          dataTestId="assessment-name-input"
                          {...register(FormFields.ASSESSMENT_NAME)}
                        />
                        <fieldset className="mt-4">
                          <div className="prose-title-input mb-2 flex text-kira-slate-700">
                            {t('Visibility')}
                            <Tooltip
                              textContent={toolTipText}
                              tooltipClassNames="z-50 left-0"
                              textClassNames="text-left whitespace-pre-line"
                            >
                              <InformationCircleIcon className="ml-1 inline-block h-3 w-3 text-kira-slate-700" />
                            </Tooltip>
                          </div>
                          <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                            {invitationMethods.map(
                              ({ id, name, value }, index) => (
                                <RadioButton
                                  name={name}
                                  id={id.toString()}
                                  checked={value === isPublic}
                                  onChange={() => setIsPublic(value)}
                                  index={index}
                                  dataTestId={name.toLocaleLowerCase()}
                                />
                              )
                            )}
                          </div>
                        </fieldset>
                      </div>
                    </div>
                    <div className="mt-6 flex w-full flex-col items-center justify-center gap-2">
                      <Button
                        variant={ButtonVariant.PRIMARY}
                        type="submit"
                        disabled={!isDirty || isLoading}
                        isLoading={isLoading}
                        buttonText={t('Create Assessment')}
                        data-testid="create-assessment"
                      />
                      <Button
                        variant={ButtonVariant.CANCEL}
                        onClick={() => navigate('/')}
                        type="button"
                        disabled={isLoading}
                        buttonText={t('Cancel')}
                        data-testid="cancel"
                      />
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateAssessmentForm;
